import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ThumbsUp, Meh, ThumbsDown, Check, Film, Tv, RefreshCw, ArrowRight, Trophy, Loader2, Tag, Clapperboard } from 'lucide-react';

export function MarkWatchedDialog({ 
  open, 
  onClose, 
  series, 
  selectedGrade, 
  onGradeSelect, 
  rankingStep, 
  newRankings,
  mediaType,
  onMoveRankUp, 
  onMoveRankDown, 
  onSaveRankings,
  // New props for first watch and comments
  isFirstWatch,
  setIsFirstWatch,
  comment,
  setComment,
  // Date prop
  watchDate,
  setWatchDate,
  // Big screen prop (for movies)
  isBigScreen,
  setIsBigScreen,
  // Genre selection props
  genres = [],
  primaryGenre,
  setPrimaryGenre,
  // New prop for proceed action (used in re-rank flow)
  onProceedToRanking,
  // 1v1 Comparison props
  comparisonStep,
  comparisonItem,
  onComparison,
  savingWatched,
  existingItemsCount = 0
}) {
  // Local state for grade selection before proceeding
  const [localGrade, setLocalGrade] = useState(selectedGrade);
  const [localPrimaryGenre, setLocalPrimaryGenre] = useState(primaryGenre || (genres.length > 0 ? genres[0] : null));
  
  // Update local grade when selectedGrade changes (e.g., when dialog opens)
  React.useEffect(() => {
    setLocalGrade(selectedGrade);
  }, [selectedGrade]);
  
  // Update local primary genre when genres or primaryGenre changes
  React.useEffect(() => {
    if (primaryGenre) {
      setLocalPrimaryGenre(primaryGenre);
    } else if (genres.length > 0) {
      setLocalPrimaryGenre(genres[0]);
    }
  }, [primaryGenre, genres]);
  
  const grades = [
    { g: 'liked', icon: <ThumbsUp className="w-6 h-6" />, label: 'I liked it', cls: 'border-[#F87171] bg-[#F87171]/5 text-[#F87171]' },
    { g: 'alright', icon: <Meh className="w-6 h-6" />, label: 'It was alright', cls: 'border-amber-400 bg-amber-50 text-amber-600' },
    { g: 'disliked', icon: <ThumbsDown className="w-6 h-6" />, label: "I didn't like it", cls: 'border-slate-400 bg-slate-50 text-slate-600' }
  ];

  const isMovie = mediaType === 'movie';
  const gradeLabels = {
    liked: 'liked',
    alright: 'thought were alright',
    disliked: "didn't like"
  };
  
  const handleGradeClick = (grade) => {
    setLocalGrade(grade);
    // If onProceedToRanking is provided (re-rank flow), don't auto-proceed
    // Otherwise (discover flow), proceed immediately
    if (!onProceedToRanking) {
      onGradeSelect(grade);
    }
  };
  
  const handleGenreSelect = (genre) => {
    setLocalPrimaryGenre(genre);
    setPrimaryGenre?.(genre);
  };
  
  const handleContinue = () => {
    if (localGrade) {
      // Set primary genre before proceeding
      if (localPrimaryGenre && setPrimaryGenre) {
        setPrimaryGenre(localPrimaryGenre);
      }
      if (onProceedToRanking) {
        onProceedToRanking(localGrade);
      } else {
        onGradeSelect(localGrade);
      }
    }
  };

  // Determine which step to show
  const showGradeSelection = !rankingStep && !comparisonStep;
  const showComparison = comparisonStep && comparisonItem;
  const showOldRanking = rankingStep && newRankings?.length > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="mark-watched-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit text-[#3D4F6F]">
            {showComparison 
              ? 'Which did you prefer?' 
              : showOldRanking 
                ? `Rank Your ${isMovie ? 'Movies' : 'TV Shows'}` 
                : 'How was it?'
            }
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            {showComparison 
              ? 'Choose which you enjoyed more to find the right ranking'
              : showOldRanking 
                ? `Order the ${isMovie ? 'movies' : 'TV shows'} you ${gradeLabels[localGrade || selectedGrade]} from best to worst`
                : 'Rate your viewing experience'
            }
          </DialogDescription>
        </DialogHeader>
        
        {/* Grade Selection Step */}
        {showGradeSelection && (
          <div className="space-y-4 py-4">
            <p className="text-[#64748B] text-center">
              How did you feel about <span className="font-semibold">{series?.title}</span>?
            </p>
            
            {/* Grade Selection */}
            <div className="space-y-2">
              {grades.map(({ g, icon, label, cls }) => (
                <button 
                  key={g} 
                  data-testid={`grade-${g}-btn`} 
                  onClick={() => handleGradeClick(g)} 
                  className={`w-full flex items-center gap-4 p-3 rounded-xl border-2 transition-all ${localGrade === g ? cls : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className={localGrade === g ? '' : 'text-slate-400'}>{icon}</div>
                  <span className={`font-medium ${localGrade === g ? '' : 'text-[#3D4F6F]'}`}>{label}</span>
                  {localGrade === g && <Check className="w-5 h-5 ml-auto" />}
                </button>
              ))}
            </div>
            
            {/* First Time Watch Toggle */}
            <div className="pt-2 border-t border-slate-100">
              <div className={`grid ${isMovie ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
                <button
                  onClick={() => setIsFirstWatch?.(!isFirstWatch)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                    isFirstWatch 
                      ? 'border-purple-400 bg-purple-50 text-purple-700' 
                      : 'border-slate-200 hover:border-slate-300 text-[#64748B]'
                  }`}
                  data-testid="first-watch-toggle"
                >
                  <RefreshCw className={`w-5 h-5 ${isFirstWatch ? 'text-purple-600' : 'text-slate-400'}`} />
                  <span className="font-medium">First time watching</span>
                  {isFirstWatch && <Check className="w-5 h-5 ml-auto text-purple-600" />}
                </button>
                
                {/* Big Screen Toggle - Movies only */}
                {isMovie && setIsBigScreen && (
                  <button
                    onClick={() => setIsBigScreen?.(!isBigScreen)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                      isBigScreen 
                        ? 'border-amber-400 bg-amber-50 text-amber-700' 
                        : 'border-slate-200 hover:border-slate-300 text-[#64748B]'
                    }`}
                    data-testid="big-screen-toggle"
                  >
                    <Clapperboard className={`w-5 h-5 ${isBigScreen ? 'text-amber-600' : 'text-slate-400'}`} />
                    <span className="font-medium">Big screen</span>
                    {isBigScreen && <Check className="w-5 h-5 ml-auto text-amber-600" />}
                  </button>
                )}
              </div>
            </div>
            
            {/* Watch Date */}
            {setWatchDate && (
              <div className="pt-2">
                <label className="text-sm font-medium text-[#64748B] mb-2 block">
                  When did you watch it?
                </label>
                <Input
                  type="date"
                  value={watchDate || ''}
                  onChange={(e) => setWatchDate(e.target.value)}
                  className="w-full"
                  data-testid="watch-date-input"
                />
              </div>
            )}
            
            {/* Comments/Thoughts */}
            <div className="pt-2">
              <label className="text-sm font-medium text-[#64748B] mb-2 block">
                Thoughts (optional)
              </label>
              <Textarea
                placeholder="What did you think about it?"
                value={comment || ''}
                onChange={(e) => setComment?.(e.target.value)}
                className="resize-none h-20"
                data-testid="watch-comment-input"
              />
            </div>
            
            {/* Primary Genre Selection */}
            {genres.length > 1 && (
              <div className="pt-2 border-t border-slate-100">
                <label className="text-sm font-medium text-[#64748B] mb-2 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Select primary genre
                </label>
                <div className="flex flex-wrap gap-2">
                  {genres.map((genre) => (
                    <button
                      key={genre}
                      type="button"
                      onClick={() => handleGenreSelect(genre)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        localPrimaryGenre === genre
                          ? 'bg-[#F87171] text-white'
                          : 'bg-slate-100 text-[#64748B] hover:bg-slate-200'
                      }`}
                      data-testid={`genre-select-${genre.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#94A3B8] mt-2">
                  This helps organize your watched history by genre
                </p>
              </div>
            )}
            
            {/* Continue Button - shown when onProceedToRanking is provided (re-rank flow) */}
            {onProceedToRanking && (
              <DialogFooter className="pt-2">
                <Button
                  onClick={handleContinue}
                  disabled={!localGrade || savingWatched}
                  className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full w-full"
                  data-testid="continue-to-ranking-btn"
                >
                  {savingWatched ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <>
                      Continue to Ranking
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </DialogFooter>
            )}
          </div>
        )}
        
        {/* 1v1 Comparison Step */}
        {showComparison && (
          <div className="py-4">
            <DialogHeader className="mb-4">
              <DialogTitle className="font-outfit text-[#3D4F6F] flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Which do you prefer?
              </DialogTitle>
              <DialogDescription className="text-[#64748B]">
                Comparing against your {gradeLabels[selectedGrade || localGrade]} ({existingItemsCount} total)
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4">
              {/* New Item */}
              <button
                onClick={() => onComparison?.(true)}
                disabled={savingWatched}
                className="group flex flex-col items-center p-4 rounded-2xl border-2 border-slate-200 hover:border-[#F87171] hover:bg-[#F87171]/5 transition-all"
                data-testid="prefer-new-btn"
              >
                <div className="relative aspect-[2/3] w-full max-w-[140px] rounded-lg overflow-hidden shadow-md mb-3">
                  {series?.poster_path ? (
                    <img src={series.poster_path} alt={series.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                      {isMovie ? <Film className="w-8 h-8 text-slate-400" /> : <Tv className="w-8 h-8 text-slate-400" />}
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-[#F87171] text-white text-xs font-bold px-2 py-1 rounded-full">
                    NEW
                  </div>
                </div>
                <p className="font-medium text-[#3D4F6F] text-center text-sm line-clamp-2">{series?.title}</p>
                <p className="text-xs text-[#64748B] mt-1">Click if you prefer this</p>
              </button>
              
              {/* Comparison Item */}
              <button
                onClick={() => onComparison?.(false)}
                disabled={savingWatched}
                className="group flex flex-col items-center p-4 rounded-2xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all"
                data-testid="prefer-existing-btn"
              >
                <div className="relative aspect-[2/3] w-full max-w-[140px] rounded-lg overflow-hidden shadow-md mb-3">
                  {comparisonItem?.poster_path ? (
                    <img src={comparisonItem.poster_path} alt={comparisonItem.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                      {isMovie ? <Film className="w-8 h-8 text-slate-400" /> : <Tv className="w-8 h-8 text-slate-400" />}
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    #{comparisonItem?.rank_in_group || '?'}
                  </div>
                </div>
                <p className="font-medium text-[#3D4F6F] text-center text-sm line-clamp-2">{comparisonItem?.title}</p>
                <p className="text-xs text-[#64748B] mt-1">Click if you prefer this</p>
              </button>
            </div>
            
            {savingWatched && (
              <div className="flex items-center justify-center gap-2 mt-6 text-[#64748B]">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving your ranking...</span>
              </div>
            )}
            
            <div className="mt-6 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-[#64748B] text-center">
                Narrowing down position... ({Math.ceil(Math.log2((existingItemsCount || 1) + 1))} comparisons max)
              </p>
            </div>
          </div>
        )}
        
        {/* Legacy Up/Down Ranking Step (fallback) */}
        {showOldRanking && (
          <div className="py-4">
            <div className="flex items-center gap-2 mb-4 p-2 bg-slate-50 rounded-lg">
              {isMovie ? (
                <Film className="w-4 h-4 text-purple-600" />
              ) : (
                <Tv className="w-4 h-4 text-blue-600" />
              )}
              <span className="text-sm text-[#64748B]">
                Ranking all {isMovie ? 'movies' : 'TV shows'} you {gradeLabels[selectedGrade]}
              </span>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {newRankings.map((item, index) => (
                <div key={item.id} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl">
                  <span className="font-outfit font-bold text-xl text-[#3D4F6F] w-8 text-center">{index + 1}</span>
                  <span className="flex-1 font-medium text-[#3D4F6F] truncate">{item.title}</span>
                </div>
              ))}
            </div>
            <DialogFooter className="mt-6">
              <Button 
                data-testid="save-rankings-btn" 
                onClick={onSaveRankings} 
                className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full w-full"
              >
                <Check className="w-4 h-4 mr-2" />
                Save Rankings
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
